import { z } from "zod";

export const CALL_INTENT_LEDGER_SCHEMA_VERSION = 1 as const;

// Bounds of the INTEGER millisecond and SMALLINT position columns the ledger persists to.
const callRelativeMsSchema = z.number().int().nonnegative().max(2_147_483_647);
const MAX_LEDGER_POSITIONS = 32_768;

export enum VoiceAction {
  ANSWER_APPOINTMENT_INQUIRY = "answer_appointment_inquiry",
  ANSWER_FAQ = "answer_faq",
  BOOK_APPOINTMENT = "book_appointment",
  CANCEL_APPOINTMENT = "cancel_appointment",
  CONFIRM_APPOINTMENT = "confirm_appointment",
  LEAVE_MESSAGE = "leave_message",
  RESCHEDULE_APPOINTMENT = "reschedule_appointment",
  ROUTE_EMERGENCY = "route_emergency",
  TRANSFER_CALL = "transfer_call",
}

export enum VoiceActionStatus {
  DECLINED = "declined",
  FAILED = "failed",
  OFFERED = "offered",
  STARTED = "started",
  SUCCEEDED = "succeeded",
}

export enum VoiceHandlingOutcome {
  HANDLED = "handled",
  NOT_HANDLED = "not_handled",
  OUT_OF_SCOPE = "out_of_scope",
}

export enum VoiceIntent {
  AUTHORIZATION_OR_REFERRAL_QUESTION = "authorization_or_referral_question",
  BILLING_OR_PRICING = "billing_or_pricing",
  CANCEL = "cancel",
  CLINICAL_QUESTION = "clinical_question",
  CONFIRM = "confirm",
  EXTENSION_DIAL = "extension_dial",
  FORMS_OR_MEDICAL_RECORDS = "forms_or_medical_records",
  GENERAL_CLINIC_QUESTION = "general_clinic_question",
  INSURANCE_QUESTION = "insurance_question",
  LANGUAGE_SUPPORT = "language_support",
  MESSAGE_FOR_STAFF = "message_for_staff",
  NON_RECORDED_LINE = "non_recorded_line",
  OTHER = "other",
  PRESCRIPTION_REFILL = "prescription_refill",
  PROVIDER_TRANSFER = "provider_transfer",
  REQUESTED_HUMAN = "requested_human",
  RESCHEDULE = "reschedule",
  SCHEDULE = "schedule",
  SPAM = "spam",
  TELEHEALTH_OR_CONNECTION_HELP = "telehealth_or_connection_help",
  TEST_RESULTS = "test_results",
  UNCONFIRMED = "unconfirmed",
  URGENT_OR_EMERGENCY = "urgent_or_emergency",
}

export enum VoiceIntentActionRole {
  FULFILLMENT = "fulfillment",
  HANDOFF = "handoff",
}

export enum VoiceIntentDisposition {
  ABANDONED = "abandoned",
  COMPLETED = "completed",
  ESCALATED = "escalated",
  WITHDRAWN = "withdrawn",
}

export enum VoiceIntentDispositionReason {
  ACTION_FAILED = "action_failed",
  CALLER_ACCEPTED_ALTERNATIVE = "caller_accepted_alternative",
  CALLER_DECLINED_ALTERNATIVE = "caller_declined_alternative",
  CALLER_DISCONNECTED = "caller_disconnected",
  CALLER_DISCONNECTED_BEFORE_ANSWERING = "caller_disconnected_before_answering",
  CALLER_SELECTED_REPLACEMENT = "caller_selected_replacement",
  HANDOFF_SUCCEEDED = "handoff_succeeded",
  NO_SUCCESSFUL_ACTION = "no_successful_action",
  POST_CALL_CLASSIFICATION = "post_call_classification",
  TRANSFER_FAILED = "transfer_failed",
  WORKFLOW_ABANDONED = "workflow_abandoned",
  WORKFLOW_COMPLETED = "workflow_completed",
  WORKFLOW_SWITCHED = "workflow_switched",
}

const WITHDRAWAL_REASONS: ReadonlySet<VoiceIntentDispositionReason> = new Set([
  VoiceIntentDispositionReason.CALLER_ACCEPTED_ALTERNATIVE,
  VoiceIntentDispositionReason.CALLER_SELECTED_REPLACEMENT,
]);

export enum VoiceIntentSource {
  ACCEPTED_FALLBACK = "accepted_fallback",
  CALLER_STATED = "caller_stated",
  KEYPAD_SELECTED = "keypad_selected",
  POST_CALL_MODEL = "post_call_model",
  SYSTEM_DETECTED = "system_detected",
}

export enum VoiceScope {
  IN_SCOPE = "in_scope",
  OUT_OF_SCOPE = "out_of_scope",
}

export enum VoiceScopeDecisionSource {
  APPOINTMENT_ACTION_SCOPE = "appointment_action_scope",
  CALL_FINALIZER = "call_finalizer",
  POST_CALL_MODEL = "post_call_model",
  ROUTING_POLICY = "routing_policy",
  SCHEDULING_ELIGIBILITY = "scheduling_eligibility",
  SCHEDULING_SCOPE = "scheduling_scope",
  WORKFLOW_RECOGNITION = "workflow_recognition",
}

export enum VoiceScopeDecisionType {
  AUTHORITATIVE = "authoritative",
  FINAL = "final",
  PROVISIONAL = "provisional",
}

export enum VoiceScopeReasonCode {
  APPOINTMENT_ACTION_BLOCKED = "appointment_action_blocked",
  APPOINTMENT_ACTION_SUPPORTED = "appointment_action_supported",
  CLINICAL_REQUEST_UNSUPPORTED = "clinical_request_unsupported",
  DEDICATED_TRANSFER_ROUTE = "dedicated_transfer_route",
  DIAGNOSIS_OR_APPOINTMENT_TYPE_BLOCKED = "diagnosis_or_appointment_type_blocked",
  INSURANCE_CLASSIFICATION_BLOCKED = "insurance_classification_blocked",
  INSURANCE_REQUIREMENT_UNMET = "insurance_requirement_unmet",
  INTENT_SUPPORTED = "intent_supported",
  INTENT_UNSUPPORTED = "intent_unsupported",
  LINKED_APPOINTMENT_RESTRICTION = "linked_appointment_restriction",
  MEDICAL_EMERGENCY_REPORTED = "medical_emergency_reported",
  MESSAGE_FALLBACK_ACCEPTED = "message_fallback_accepted",
  POST_CALL_CLASSIFICATION = "post_call_classification",
  PROVIDER_RESTRICTION = "provider_restriction",
  PROVISIONAL_AT_CALL_END = "provisional_at_call_end",
  REFERRAL_OR_PRIOR_VISIT_REQUIRED = "referral_or_prior_visit_required",
  SCHEDULING_ELIGIBLE = "scheduling_eligible",
  SCHEDULING_INELIGIBLE = "scheduling_ineligible",
  SCHEDULING_SCOPE_ALLOWED = "scheduling_scope_allowed",
  SCHEDULING_SCOPE_BLOCKED = "scheduling_scope_blocked",
  TEST_OR_PROCEDURE_BLOCKED = "test_or_procedure_blocked",
  UNCONFIRMED_AT_CALL_END = "unconfirmed_at_call_end",
  UPCOMING_APPOINTMENT_RESTRICTION = "upcoming_appointment_restriction",
  URGENT_WINDOW_UNAVAILABLE = "urgent_window_unavailable",
  WRONG_CLINIC_OR_SITE = "wrong_clinic_or_site",
}

export const voiceScopeDecisionSchema = z.object({
  decisionType: z.enum(VoiceScopeDecisionType),
  occurredAtMs: callRelativeMsSchema,
  reasonCode: z.enum(VoiceScopeReasonCode),
  scope: z.enum(VoiceScope),
  source: z.enum(VoiceScopeDecisionSource),
});

export const callIntentOccurrenceSchema = z
  .object({
    actionIds: z.array(z.string().uuid()),
    detectedAtMs: callRelativeMsSchema,
    disposition: z.enum(VoiceIntentDisposition),
    dispositionReason: z.enum(VoiceIntentDispositionReason),
    finalScope: z.enum(VoiceScope),
    id: z.string().uuid(),
    intent: z.enum(VoiceIntent),
    parentIntentId: z.string().uuid().nullable(),
    scopeEvents: z.array(voiceScopeDecisionSchema).min(1).max(MAX_LEDGER_POSITIONS),
    source: z.enum(VoiceIntentSource),
  })
  .superRefine((occurrence, context) => {
    if (new Set(occurrence.actionIds).size !== occurrence.actionIds.length) {
      context.addIssue({ code: "custom", message: "Intent action IDs must be unique", path: ["actionIds"] });
    }

    const finalEvent = occurrence.scopeEvents.at(-1);

    if (finalEvent?.decisionType !== VoiceScopeDecisionType.FINAL) {
      context.addIssue({
        code: "custom",
        message: "The final scope event must finalize the occurrence",
        path: ["scopeEvents"],
      });
    } else if (finalEvent.scope !== occurrence.finalScope) {
      context.addIssue({
        code: "custom",
        message: "The final scope event must match finalScope",
        path: ["finalScope"],
      });
    }
  });

export const callActionSchema = z.object({
  action: z.enum(VoiceAction),
  id: z.string().uuid(),
  intentLinks: z
    .array(
      z.object({
        intentId: z.string().uuid(),
        role: z.enum(VoiceIntentActionRole),
      })
    )
    .min(1),
  occurredAtMs: callRelativeMsSchema,
  status: z.enum(VoiceActionStatus),
});

const callIntentOccurrenceSetFields = {
  schemaVersion: z.literal(CALL_INTENT_LEDGER_SCHEMA_VERSION),
  occurrences: z.array(callIntentOccurrenceSchema).min(1).max(MAX_LEDGER_POSITIONS),
} as const;

type IntentOccurrence = z.infer<typeof callIntentOccurrenceSchema>;

function occurrenceSetReferenceIssues(occurrences: readonly IntentOccurrence[]) {
  const occurrenceIndexById = new Map<string, number>();
  const issues: Array<{ message: string; path: (string | number)[] }> = [];

  for (const [index, occurrence] of occurrences.entries()) {
    if (occurrenceIndexById.has(occurrence.id)) {
      issues.push({ message: "Intent occurrence IDs must be unique", path: ["occurrences", index, "id"] });
    } else {
      occurrenceIndexById.set(occurrence.id, index);
    }
  }

  const unconfirmedIndex = occurrences.findIndex(({ intent }) => intent === VoiceIntent.UNCONFIRMED);

  if (unconfirmedIndex >= 0 && occurrences.length !== 1) {
    issues.push({
      message: "Unconfirmed cannot be combined with identified intent occurrences",
      path: ["occurrences", unconfirmedIndex, "intent"],
    });
  }

  for (const [index, occurrence] of occurrences.entries()) {
    if (occurrence.intent === VoiceIntent.UNCONFIRMED) {
      if (occurrence.disposition !== VoiceIntentDisposition.ABANDONED) {
        issues.push({
          message: "An unconfirmed occurrence must be abandoned",
          path: ["occurrences", index, "disposition"],
        });
      }

      if (occurrence.finalScope !== VoiceScope.OUT_OF_SCOPE) {
        issues.push({
          message: "An unconfirmed occurrence must be out of scope",
          path: ["occurrences", index, "finalScope"],
        });
      }
    }

    if (
      (occurrence.disposition === VoiceIntentDisposition.WITHDRAWN) !==
      WITHDRAWAL_REASONS.has(occurrence.dispositionReason)
    ) {
      issues.push({
        message: "Only a withdrawn occurrence uses, and must use, a replacement reason",
        path: ["occurrences", index, "dispositionReason"],
      });
    }

    if (!occurrence.parentIntentId) continue;

    const parentIndex = occurrenceIndexById.get(occurrence.parentIntentId);

    if (parentIndex === undefined) {
      issues.push({
        message: "Parent intent must reference another occurrence in the set",
        path: ["occurrences", index, "parentIntentId"],
      });
    } else if (parentIndex >= index) {
      issues.push({
        message: "A parent intent must appear before its fallback occurrence",
        path: ["occurrences", index, "parentIntentId"],
      });
    }

    if (occurrence.intent !== VoiceIntent.MESSAGE_FOR_STAFF) {
      issues.push({
        message: "Only a staff-message fallback may reference a parent intent",
        path: ["occurrences", index, "parentIntentId"],
      });
    }
  }

  return issues;
}

export const callIntentOccurrenceSetSchema = z.object(callIntentOccurrenceSetFields).superRefine((set, context) => {
  for (const issue of occurrenceSetReferenceIssues(set.occurrences)) {
    context.addIssue({ code: "custom", ...issue });
  }
});

export const callIntentLedgerSchema = z
  .object({
    ...callIntentOccurrenceSetFields,
    actions: z.array(callActionSchema).max(MAX_LEDGER_POSITIONS),
    policyVersion: z.string().trim().min(1).max(128),
  })
  .superRefine((ledger, context) => {
    for (const issue of occurrenceSetReferenceIssues(ledger.occurrences)) {
      context.addIssue({ code: "custom", ...issue });
    }

    const occurrenceById = new Map(ledger.occurrences.map((occurrence) => [occurrence.id, occurrence]));
    const actionById = new Map(ledger.actions.map((action) => [action.id, action]));

    if (actionById.size !== ledger.actions.length) {
      context.addIssue({ code: "custom", message: "Action IDs must be unique", path: ["actions"] });
    }

    for (const [occurrenceIndex, occurrence] of ledger.occurrences.entries()) {
      for (const [actionIndex, actionId] of occurrence.actionIds.entries()) {
        const action = actionById.get(actionId);

        if (!action?.intentLinks.some(({ intentId }) => intentId === occurrence.id)) {
          context.addIssue({
            code: "custom",
            message: "Intent action IDs must reference actions linked back to the occurrence",
            path: ["occurrences", occurrenceIndex, "actionIds", actionIndex],
          });
        }

        if (
          occurrence.disposition === VoiceIntentDisposition.WITHDRAWN &&
          action?.status === VoiceActionStatus.SUCCEEDED &&
          action.intentLinks.some(
            ({ intentId, role }) => intentId === occurrence.id && role === VoiceIntentActionRole.FULFILLMENT
          )
        ) {
          context.addIssue({
            code: "custom",
            message: "A withdrawn occurrence cannot have successful fulfillment evidence",
            path: ["occurrences", occurrenceIndex, "actionIds", actionIndex],
          });
        }
      }
    }

    for (const [actionIndex, action] of ledger.actions.entries()) {
      const linkedIntentIds = action.intentLinks.map(({ intentId }) => intentId);

      if (new Set(linkedIntentIds).size !== linkedIntentIds.length) {
        context.addIssue({
          code: "custom",
          message: "An action can link to an intent occurrence only once",
          path: ["actions", actionIndex, "intentLinks"],
        });
      }

      for (const [linkIndex, link] of action.intentLinks.entries()) {
        const occurrence = occurrenceById.get(link.intentId);

        if (!occurrence?.actionIds.includes(action.id)) {
          context.addIssue({
            code: "custom",
            message: "Action links must reference an occurrence that lists the action",
            path: ["actions", actionIndex, "intentLinks", linkIndex],
          });
        }
      }
    }
  });

export type CallAction = z.infer<typeof callActionSchema>;
export type CallIntentLedger = z.infer<typeof callIntentLedgerSchema>;
export type CallIntentOccurrence = z.infer<typeof callIntentOccurrenceSchema>;
export type CallIntentOccurrenceSet = z.infer<typeof callIntentOccurrenceSetSchema>;
export type VoiceScopeDecision = z.infer<typeof voiceScopeDecisionSchema>;
